import { makeAutoObservable } from 'mobx';
import { message } from '@ss/mtd-react';
import {
  IPositionDetail,
  IPositionDeliveryStatus,
  IDeliveryInfo,
  IBindBoleInfo,
  IBoleInfo,
  defaultBoleInfo,
} from '@src/types';
import { getJobDetail, getBoleInfo, getShareInfo, getDeliveryInfo, getDeliveryStatus } from '@src/service';
import { isCampusFn } from '@utils/formatData';
import { JOB_SPECIAL_CODE, JOB_STATUS, SPECIAL_BTN_TIP } from '@src/enums';

export default class Delivery {
  loading = false;

  hasBindBole = false; // 是否绑定伯乐

  detail: IPositionDetail;

  applyStatus: IPositionDeliveryStatus;

  deliveryInfo: IDeliveryInfo;

  /**
   * @description: 已绑定的伯乐信息，校招绑定过之后不能换绑,对应移动端bindBoleInfo
   * @return {*}
   */
  bindBoleInfo: IBindBoleInfo;

  /**
   * @description: 伯乐信息,根据token && staffSsoId 获取的
   * @return {*}
   */
  boleInfo: IBoleInfo;

  deliveryBtnInfo = {
    btnName: '投递',
    btnDisabled: false,
    btnTips: '',
  };

  constructor() {
    makeAutoObservable(this);
  }

  getInitData = async ({ jobUnionId, token, staffSsoId, jobShareType }, go) => {
    let boleInfo: IBoleInfo = defaultBoleInfo;
    // @ts-ignore
    let bindBoleInfo: IBindBoleInfo = {};
    try {
      this.setValue('loading', true);
      // 获取职位详情
      const detail = (await getJobDetail({ jobUnionId, jobShareType })) || {};
      const { projectId } = detail;
      const isCampus = isCampusFn(detail?.jobSpecialCode);
      // 校招感兴趣事业群
      if (isCampus) {
        if (Array.isArray(detail?.department) && detail?.department.length > 1) {
          detail.department = [{ code: '0', name: '都喜欢' }].concat(detail.department);
        }
      }
      this.setValue('detail', detail);
      this.setValue('loading', false);
      // 获取投递状态、历史投递信息
      await Promise.allSettled([
        getDeliveryStatus({ jobId: jobUnionId }),
        getDeliveryInfo({ projectId, jobUnionId }),
      ]).then((args) => {
        // @ts-ignore
        const applyStatus = args[0].value || {};
        // @ts-ignore
        const deliveryInfo = args[1].value;
        this.formatDeliveryBtn(
          isCampus || detail?.jobSpecialCode === JOB_SPECIAL_CODE.XIANGMUSHIXI,
          applyStatus,
          detail,
        );

        this.setValue('applyStatus', applyStatus);
        this.setValue('deliveryInfo', deliveryInfo);
      });
      // 校招===>是否绑定伯乐（type）
      if (isCampus) {
        // 是否绑定伯乐
        bindBoleInfo = await getBoleInfo({ projectId, jobUnionId });
        if (bindBoleInfo?.recommendationNum) {
          // 已绑定伯乐
          this.hasBindBole = true;
          // 未绑定 依据token获取伯乐信息
        } else {
          // 伯乐没有数据, 就是没有绑定伯乐, 要更新mobx数据, 防止缓存
          this.hasBindBole = false;
          const { isApplied, isVolunteerFull, haveIntentionJob } = this.applyStatus || {};
          const isView = isVolunteerFull || !haveIntentionJob || isApplied; // 查看申请(true)按钮过来的不需要跳转，立即申请需要(false)
          if (token || staffSsoId) {
            const res = await this.getShareInfo({ token, staffSsoId, jobShareType }, go, !isView);
            boleInfo = res;
          }
        }
      }
      // 社招 链接上有token 获取伯乐信息
      if (!isCampus && (token || staffSsoId)) {
        const res = await this.getShareInfo({ token, staffSsoId, jobShareType }, go);
        boleInfo = res;
      }
      // 一次修改两个值,不用setValue,防止多次触发页面渲染,导致一些监听数据接口重复调用
      this.setBoleInfoAndBindBoleInfo(boleInfo, bindBoleInfo);
    } catch (error) {
      console.log('error===>', error);
    } finally {
      this.setValue('loading', false);
    }
  };

  getShareInfo = async ({ jobShareType, token, staffSsoId }, go, needSkip = true) => {
    const shareInfo =
      (await getShareInfo({
        jobShareType, // 职位分享类型
        token,
        staffSsoId,
      })) || {};
    if (!shareInfo?.isEffective) {
      if (needSkip) {
        message.error({
          message: '该链接已失效，即将跳转到美团招聘官网首页',
          duration: 2000,
        });
        setTimeout(() => {
          go('/home', { replace: true });
        }, 2000);
      }
      return {
        boleMis: '',
        boleName: '',
        recommendationNum: '',
      };
    }
    return {
      boleMis: shareInfo?.misId,
      boleName: shareInfo?.userName,
      recommendationNum: shareInfo?.recommendationNum,
    };
  };

  // 格式化投递按钮
  formatDeliveryBtn = (isCampus, applyStatus, detail) => {
    // const { canApply, isApplied, applyJobStep, jobName } = applyStatus || {};
    const { canApply, isApplied, haveIntentionJob } = applyStatus || {};
    let btnName = '投递';
    let btnTips = '';
    let btnDisabled = false;

    if (detail?.jobStatus !== JOB_STATUS.IN_RECRUITMENT) {
      btnTips = '当前职位无法投递';
      btnDisabled = true;
    } else if (isCampus) {
      btnTips = detail?.jobSpecialCode === JOB_SPECIAL_CODE.XIANGMUSHIXI ? '' : SPECIAL_BTN_TIP;
      // : '1.最多可投递3个职位，建议根据自己的兴趣和职位描述，尽量填满；\n 2.职位投递后，会按照志愿顺序流转，志愿不可修改，请谨慎选择；\n 3.你的毕业时间需要在此次校招的范围内，请关注。';
      if (!canApply) {
        btnTips = '当前职位无法投递';
        btnDisabled = true;
      } else if (haveIntentionJob === false) {
        // 勾选了【无明确意向职位】并已经投递过,不提示上面那个文案
        btnTips = '';
      }
    } else if (!isCampus) {
      if (isApplied) {
        btnName = '已投递';
        btnDisabled = true;
      }
    }

    this.setValue('deliveryBtnInfo', {
      btnName,
      btnTips,
      btnDisabled,
    });
  };

  setValue = (key, payload) => {
    this[key] = payload;
  };

  setBoleInfoAndBindBoleInfo = (boleInfo, bindBoleInfo) => {
    this.boleInfo = boleInfo;
    this.bindBoleInfo = bindBoleInfo;
  };
}
